import React, { memo, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
// evertel
import {
    Card, CardBody, ContentPlaceholder,
    Row, Col, Text, Badge, Media
} from '@evertel/web/ui'
import { DocumentViewController } from '@evertel/document'
import { DocumentIcon } from './DocumentIcon'
import { DepartmentController, DepartmentStore } from '@evertel/department'
import { useService } from '@evertel/di'
import { APIDataMedia } from '@evertel/types'
import { getMediaType } from '@evertel/utils'
import { APIDataMediaToNormalizedMedia } from '@evertel/media'


interface TileProps {
    documentId: number
    onClick?: (documentId: number | undefined) => void
    className?: string
}

const DocumentTile: React.FC<TileProps> = observer(({
    documentId,
    onClick,
    className
}) => {

    const documentViewController = useService(DocumentViewController, [documentId])
    const departmentController = useService(DepartmentController, [documentId])

    useEffect(() => {
        (async () => {
            await documentViewController.loadFromStoreOrFetch(documentId)
            if (documentViewController.document) {
                departmentController.init(documentViewController.document.departmentId)
            }
        })()
    }, [documentId])

    const document = documentViewController.document
    const schema = documentViewController.documentSchema
    const department = departmentController.department

    //used in place of isLoading
    if (!documentViewController?.document) {
        return (
            <Card
                className={classNames('document-tile', className)}
                style={{
                    borderLeftColor: '#999',
                    borderLeftWidth: 5
                }}>
                <CardBody>
                    <ContentPlaceholder
                        width="70%"
                        color="gray200"
                        className="mt-1"
                    />
                    <Row className="mt-2">
                        <ContentPlaceholder
                            width={20}
                            height={26}
                            color="gray200"
                            className="me-2"
                        />
                        <ContentPlaceholder
                            width="40%"
                            color="gray200"
                            className="mt-1"
                        />
                    </Row>
                </CardBody>
            </Card>
        )
    }

    const images: APIDataMedia[] = documentViewController?.document?.media?.filter(m => getMediaType(m, false) === 'image') // just grab images

    const subtext = `${schema?.name ?? ''}${department ? ` (${department.shortName})` : ''}`

    return (
        <Card
            className={classNames('document-tile', className)}
            onClick={() => onClick?.(document.id)}
            as="button"
            style={{
                borderLeftColor: schema?.color,
                borderLeftWidth: 5
            }}>
            <CardBody>
                <Row>
                    <Col
                        xs={8}
                        className="p-0"
                    >
                        {(document?.isArchived) &&
                            <Row>
                                <Badge color="muted">
                                    Archived
                                </Badge>
                            </Row>
                        }
                        <Row>
                            <Text bold style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {document.name || 'Untitled Document'}
                            </Text>
                        </Row>
                        <Row className="mt-2">
                            <DocumentIcon
                                schema={schema}
                            />
                            <Text
                                size={12}
                                className="ms-2"
                                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                title={subtext}
                            >
                                {subtext}
                            </Text>
                        </Row>
                    </Col>
                    <Col
                        xs={2}
                        align="right"
                        className="p-0"
                    >
                        {(!!images?.length) &&
                            <Media
                                media={APIDataMediaToNormalizedMedia(images[0])}
                                width={50}
                                height={50}
                                imageProps={{ flexibility: 'fixed', resizeMode: 'cover' }}
                                rounded
                            />
                        }
                    </Col>
                </Row>
            </CardBody>
        </Card>
    )
})

export {
    DocumentTile
}
